import { defineStore } from 'pinia'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore'
import { useNuxtApp } from '#app'
import { toRaw } from 'vue'

export interface ProductSEO {
    primaryKeyword: string
    secondaryKeywords: string[]
    seoTitle: string
    metaDescription: string
    slug: string
    imageAltText: string
    canonicalUrl: string
    schemaDescription: string
    searchTags: string[]
    seoScore: number
}

export interface Product {
    id: string | number
    name: string
    slug: string
    description: string
    price: number
    category: string
    image: string
    stock: number
    status: 'In Stock' | 'Low Stock' | 'Out of Stock'
    featured?: boolean
    bestSeller?: boolean
    rating?: number
    futureProduct?: boolean
    offerProduct?: boolean
    offerPrice?: number
    brand?: string
    subImages?: string[]
    features?: string[]
    technicalSpecs?: Record<string, string>
    documents?: { name: string, url: string }[]
    sku?: string
    seo?: ProductSEO
}

export const categoryIcons: Record<string, string> = {
    'Fire Extinguishers': 'Flame',
    'Smoke Detectors': 'Shield',
    'Fire Alarms': 'Zap',
    'Fire Hoses': 'Wind',
    'Fire Blankets': 'Layers',
    'First Aid': 'HeartPulse',
    'Emergency Lights': 'Lightbulb',
    'Sprinkler Systems': 'Droplets'
}

export const categoryImages: Record<string, string> = {
    'Fire Extinguishers': 'https://images.unsplash.com/photo-1610492484606-9051cc5e717e?auto=format&fit=crop&q=80&w=800',
    'Smoke Detectors': 'https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80&w=800',
    'Fire Alarms': 'https://images.unsplash.com/photo-1599423300746-b62533397364?auto=format&fit=crop&q=80&w=800',
    'Fire Hoses': 'https://images.unsplash.com/photo-1510018572596-e40e2411b82a?auto=format&fit=crop&q=80&w=800',
    'Sprinkler Systems': 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=800',
    'Emergency Lights': 'https://images.unsplash.com/photo-1574744349216-79719c215372?auto=format&fit=crop&q=80&w=800',
    'Fire Blankets': 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800',
    'First Aid': 'https://images.unsplash.com/photo-1603398938378-e54eab446f08?auto=format&fit=crop&q=80&w=800'
}

export interface Brand {
    id: string
    name: string
    logo: string
}

const cleanForFirebase = (obj: any): any => {
    // 1. Get raw object if it's a Vue proxy
    const raw = toRaw(obj)

    // 2. Handle primitives
    if (raw === undefined) return undefined
    if (raw === null) return null
    if (typeof raw !== 'object') return raw

    // 3. Handle Dates (Firestore native support)
    if (raw instanceof Date) return raw

    // 4. Handle Arrays
    if (Array.isArray(raw)) {
        return raw
            .map(item => {
                const cleaned = cleanForFirebase(item)
                // Firestore DOES NOT allow nested arrays. 
                // If we found a nested array, we'll stringify it to avoid the "invalid nested entity" error.
                if (Array.isArray(cleaned)) {
                    console.warn("Nested array detected and stringified to prevent Firebase error:", cleaned)
                    return JSON.stringify(cleaned)
                }
                return cleaned
            })
            .filter(item => item !== undefined)
    }

    // 5. Handle Objects
    const cleaned: Record<string, any> = {}
    let hasKeys = false

    for (const [key, value] of Object.entries(raw)) {
        // Skip internal/invalid keys
        if (key.startsWith('__') || key === 'id') continue

        const cleanedValue = cleanForFirebase(value)
        if (cleanedValue !== undefined) {
            cleaned[key] = cleanedValue
            hasKeys = true
        }
    }

    return hasKeys ? cleaned : (Object.keys(raw).length === 0 ? {} : undefined)
}

export const useProductStore = defineStore('products', {
    state: () => ({
        products: [] as Product[],
        categories: [
            'Fire Extinguishers',
            'Smoke Detectors',
            'Fire Alarms',
            'Fire Hoses',
            'Sprinkler Systems',
            'Emergency Lights',
            'Fire Blankets',
            'First Aid'
        ] as string[],
        brands: [] as Brand[],
        loading: false,
        error: null as string | null
    }),

    getters: {
        featuredProducts: (state) => state.products.filter(p => p.featured),
        bestSellers: (state) => state.products.filter(p => p.bestSeller),
        getProductBySlug: (state) => (slug: string) => state.products.find(p => p.slug === slug || p.id === slug || p.seo?.slug === slug),
        getProductsByCategory: (state) => (category: string) =>
            category === 'All' ? state.products : state.products.filter(p => p.category === category)
    },

    actions: {
        async fetchCategories() {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const querySnapshot = await getDocs(collection($db, 'categories'))
                    const categoriesList: string[] = []
                    querySnapshot.forEach((doc) => {
                        const data = doc.data()
                        categoriesList.push(data.name)
                        if (data.icon) {
                            categoryIcons[data.name] = data.icon
                        }
                        if (data.image) {
                            categoryImages[data.name] = data.image
                        }
                    })
                    if (categoriesList.length > 0) {
                        this.categories = categoriesList
                    }
                }
            } catch (err) {
                console.error('Error fetching categories:', err)
            }
        },

        async fetchProducts() {
            this.loading = true
            try {
                if (process.client) {
                    await this.fetchCategories()
                    const { $db } = useNuxtApp() as any
                    const querySnapshot = await getDocs(collection($db, 'products'))
                    const productsList: Product[] = []
                    querySnapshot.forEach((doc) => {
                        const data = doc.data()
                        const product = {
                            id: doc.id,
                            ...data,
                            // Ensure slug exists, fallback to name-based slug if missing
                            slug: data.slug || (data.name ? data.name.toLowerCase().replace(/\s+/g, '-') : doc.id),
                            // Ensure price is a number, fallback to 0 if missing or invalid
                            price: typeof data.price === 'number' ? data.price : Number(data.price) || 0
                        } as Product
                        productsList.push(product)
                    })

                    if (productsList.length > 0) {
                        this.products = productsList
                    } else {
                        // Keep fallback if database is completely empty
                        this.products = []
                    }
                }
            } catch (err) {
                console.error('Error fetching products from Firebase', err)
                this.error = 'Failed to fetch products'
            } finally {
                this.loading = false
            }
        },

        async fetchBrands() {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const querySnapshot = await getDocs(collection($db, 'brands'))
                    const brandsList: Brand[] = []
                    querySnapshot.forEach((doc) => {
                        brandsList.push({ id: doc.id, ...doc.data() } as Brand)
                    })
                    this.brands = brandsList
                }
            } catch (err) {
                console.error('Error fetching brands:', err)
            }
        },

        async addBrand(brand: Omit<Brand, 'id'>) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const docRef = await addDoc(collection($db, 'brands'), brand)
                    const newBrand = { id: docRef.id, ...brand }
                    this.brands.push(newBrand)
                    return newBrand
                }
            } catch (err) {
                console.error('Error adding brand:', err)
            }
        },

        async deleteBrand(id: string) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    await deleteDoc(doc($db, 'brands', id))
                    this.brands = this.brands.filter(b => b.id !== id)
                }
            } catch (err) {
                console.error('Error deleting brand:', err)
            }
        },

        async addProduct(product: Omit<Product, 'id' | 'rating'>) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const cleanedProduct = cleanForFirebase(product)
                    const docRef = await addDoc(collection($db, 'products'), {
                        ...cleanedProduct,
                        rating: 0
                    })

                    const newProduct: Product = {
                        ...cleanedProduct,
                        id: docRef.id,
                        rating: 0
                    }
                    this.products.unshift(newProduct)
                    return newProduct
                }
            } catch (err) {
                console.error("Error adding product: ", err)
            }
        },

        async updateProduct(id: string | number, updates: Partial<Product>) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const productRef = doc($db, 'products', id.toString())

                    // Recursive cleaner to remove undefined and ensure plain objects
                    const cleanUpdates = cleanForFirebase(updates)

                    if (!cleanUpdates || Object.keys(cleanUpdates).length === 0) {
                        console.warn("No valid updates found for product:", id)
                        return
                    }

                    console.log("Saving clean updates to Firebase:", JSON.parse(JSON.stringify(cleanUpdates)))

                    // Using updateDoc instead of setDoc for partial updates
                    // This is generally safer for nested objects if we want to avoid overwriting them
                    await updateDoc(productRef, cleanUpdates)

                    const index = this.products.findIndex(p => p.id === id)
                    if (index !== -1) {
                        this.products[index] = { ...this.products[index], ...cleanUpdates } as Product
                    }
                }
            } catch (err) {
                console.error("Error updating product: ", err)
                throw err // Re-throw to handle in UI if needed
            }
        },

        async deleteProduct(id: string | number) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    await deleteDoc(doc($db, 'products', id.toString()))
                    this.products = this.products.filter(p => p.id !== id)
                }
            } catch (err) {
                console.error("Error deleting product: ", err)
            }
        },

        async addCategory(name: string, iconName?: string, imageUrl?: string) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const categoryId = name.toLowerCase().replace(/\s+/g, '-')
                    const categoryData = {
                        name,
                        icon: iconName || 'Package',
                        image: imageUrl || categoryImages[name] || 'https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80&w=800'
                    }
                    await setDoc(doc($db, 'categories', categoryId), categoryData)

                    if (!this.categories.includes(name)) {
                        this.categories.push(name)
                    }
                    categoryIcons[name] = categoryData.icon
                    categoryImages[name] = categoryData.image
                }
            } catch (err) {
                console.error("Error adding category: ", err)
            }
        },

        async deleteCategory(name: string) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const categoryId = name.toLowerCase().replace(/\s+/g, '-')
                    await deleteDoc(doc($db, 'categories', categoryId))
                    this.categories = this.categories.filter(c => c !== name)
                    delete categoryIcons[name]
                    delete categoryImages[name]
                }
            } catch (err) {
                console.error("Error deleting category: ", err)
            }
        },

        async updateCategory(oldName: string, newName: string, iconName?: string, imageUrl?: string) {
            try {
                if (process.client) {
                    const { $db } = useNuxtApp() as any
                    const oldCategoryId = oldName.toLowerCase().replace(/\s+/g, '-')
                    const newCategoryId = newName.toLowerCase().replace(/\s+/g, '-')
                    
                    const updatedData = {
                        name: newName,
                        icon: iconName || categoryIcons[oldName] || 'Package',
                        image: imageUrl || categoryImages[oldName] || 'https://images.unsplash.com/photo-1599839619722-39751411ea63?auto=format&fit=crop&q=80&w=800'
                    }

                    if (oldName !== newName) {
                        await setDoc(doc($db, 'categories', newCategoryId), updatedData)
                        
                        const productsToUpdate = this.products.filter(p => p.category === oldName)
                        for (const product of productsToUpdate) {
                            await this.updateProduct(product.id, { category: newName })
                        }

                        await deleteDoc(doc($db, 'categories', oldCategoryId))

                        this.categories = this.categories.map(c => c === oldName ? newName : c)
                        delete categoryIcons[oldName]
                        delete categoryImages[oldName]
                    } else {
                        await updateDoc(doc($db, 'categories', oldCategoryId), updatedData)
                    }
                    
                    categoryIcons[newName] = updatedData.icon
                    categoryImages[newName] = updatedData.image
                }
            } catch (err) {
                console.error("Error updating category: ", err)
                throw err
            }
        }
    }
})
